/* ===========================================================================
   HEYBABE Atelier — commerce
   Cart, variants, quantity, drawers, predictive search, wishlist, quick view.

   This file never reads window.HB_MOTION and never touches a reveal class.
   Motion never reads cart state. The two layers cannot interfere.

   Every request uses Shopify's documented endpoints. No theme-invented API.
   =========================================================================== */

(function () {
  'use strict';

  var HB = window.HB || {};
  var routes = HB.routes || {};
  var strings = HB.strings || {};
  var settings = HB.settings || {};

  /* The product form is addressed by its Shopify-generated id, not by a
     custom data attribute.

     The {% form %} tag's `data:` argument emits data="hb-product-form", not
     data-hb-product-form, so a selector written for the latter never matched
     anything. Every product interaction that needs the form — variant
     switching, quantity, quick add, and the mobile sticky purchase bar —
     silently did nothing. ProductForm is the id Shopify itself assigns and
     guarantees, so it is the stable anchor. */
  var PRODUCT_FORM = '#ProductForm';

  /* ---------------------------------------------------------------------
     Utilities
     --------------------------------------------------------------------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function money(cents) {
    if (typeof cents !== 'number') cents = Number(cents) || 0;
    var format = (window.HB && window.HB.settings && window.HB.settings.moneyFormat) || '{{amount_with_comma_separator}} TL';
    
    function formatWithDelimiters(number, precision, thousands, decimal) {
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      var centsPart = parts[1] ? (decimal + parts[1]) : '';
      return dollars + centsPart;
    }

    function fallback() {
      try {
        return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100) + ' TL';
      } catch (_) {
        return (cents / 100).toFixed(2).replace('.', ',') + ' TL';
      }
    }

    if (!format || typeof format !== 'string') {
      return fallback();
    }

    try {
      if (format.indexOf('{{amount_no_decimals_with_comma_separator}}') !== -1) {
        return format.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, formatWithDelimiters(cents, 0, '.', ','));
      }
      if (format.indexOf('{{amount_with_comma_separator}}') !== -1) {
        return format.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, formatWithDelimiters(cents, 2, '.', ','));
      }
      if (format.indexOf('{{amount_no_decimals}}') !== -1) {
        return format.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, formatWithDelimiters(cents, 0, ',', '.'));
      }
      if (format.indexOf('{{amount}}') !== -1) {
        return format.replace(/\{\{\s*amount\s*\}\}/g, formatWithDelimiters(cents, 2, ',', '.'));
      }
      if (/\{\{.*?\}\}/.test(format)) {
        return fallback();
      }
      return format;
    } catch (e) {
      return fallback();
    }
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function shopifyImgUrl(src, width) {
    if (!src || typeof src !== 'string') return '';
    width = width || 240;
    // Remove existing width param if present
    var clean = src.replace(/([?&])width=\d+(&?)/, function (_, p1, p2) {
      return p2 ? p1 : '';
    });
    var separator = clean.indexOf('?') === -1 ? '?' : '&';
    return clean + separator + 'width=' + width;
  }

  function announce(msg) {
    var live = $('#hb-live');
    if (!live) {
      live = document.createElement('div');
      live.id = 'hb-live';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.className = 'sr-only';
      document.body.appendChild(live);
    }
    live.textContent = '';
    window.setTimeout(function () { live.textContent = msg; }, 60);
  }

  window.HBAnnounce = announce;

  /* Commerce owns the truth and publishes it. Decoration listens; a broken
     listener must never be able to break a purchase, hence the try/catch. */
  function emit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    } catch (e) { /* decoration only */ }
  }

  /* One state contract for every purchase control: idle | loading | success | error.
     hb-motion.css draws the states; this only records them. */
  var Feedback = {
    set: function (btn, state, label) {
      if (!btn) return;
      btn.setAttribute('data-hb-feedback', '');
      btn.setAttribute('data-state', state);
      if (state === 'loading') btn.setAttribute('aria-busy', 'true');
      else btn.removeAttribute('aria-busy');
      if (label != null) {
        btn.textContent = label;
        if (state === 'success') {
          btn.insertAdjacentHTML('beforeend',
            '<svg class="hb-glyph hb-feedback__check" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
            '<path pathLength="1" d="m4.5 12.5 5 5 10-11" stroke="currentColor" stroke-width="1.5"/></svg>');
        }
      }
    }
  };
  window.HBFeedback = Feedback;

  function store(key, val) {
    try {
      if (val === undefined) return JSON.parse(localStorage.getItem(key) || 'null');
      if (val === null) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify(val));
    } catch (e) { return null; }
    return null;
  }

  /* ---------------------------------------------------------------------
     Drawers — native <dialog>
     Focus, escape, backdrop and scroll lock belong to the browser. The one
     thing we add is restoring focus to whatever opened the drawer.
     --------------------------------------------------------------------- */
  var Drawer = {
    lastFocus: null,

    open: function (name) {
      var el = document.querySelector('[data-hb-drawer="' + name + '"]');
      if (!el) return;

      // Close any other open drawers first so they never overlap. Focus is not
      // returned during a hand-off; the original opener is kept instead.
      $$('dialog[open]').forEach(function (openDialog) {
        if (openDialog !== el) {
          Drawer.close(openDialog, true);
        }
      });

      var active = document.activeElement;
      if (active && active !== document.body && !(active.closest && active.closest('dialog'))) {
        Drawer.lastFocus = active;
      }
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
      document.documentElement.style.setProperty('overflow', 'hidden');
      document.body.classList.add('has-open-drawer');

      // Make main content and footer inert while drawer is open
      var mainEl = document.querySelector('#MainContent');
      if (mainEl) mainEl.setAttribute('inert', '');
      var footEl = document.querySelector('footer, .page__footer, [role="contentinfo"]');
      if (footEl) footEl.setAttribute('inert', '');

      var head = document.querySelector('[data-hb-header]');
      if (head) head.classList.remove('is-header-hidden');
      $$('[data-hb-drawer-open="' + name + '"]').forEach(function (t) {
        t.setAttribute('aria-expanded', 'true');
      });
      var focusable = el.querySelector('[data-autofocus], button, input, a[href]');
      if (focusable) focusable.focus();
    },

    close: function (el, handOff) {
      el = el || document.querySelector('dialog[open]');
      if (!el) return;
      if (typeof el.close === 'function') el.close();
      else el.removeAttribute('open');

      // Check if any other drawer is still open
      var anyOpen = document.querySelector('dialog[open]');
      if (!anyOpen) {
        document.documentElement.style.removeProperty('overflow');
        document.body.classList.remove('has-open-drawer');
        var mainEl = document.querySelector('#MainContent');
        if (mainEl) mainEl.removeAttribute('inert');
        var footEl = document.querySelector('footer, .page__footer, [role="contentinfo"]');
        if (footEl) footEl.removeAttribute('inert');
      }

      $$('[data-hb-drawer-open]').forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
      if (!handOff && Drawer.lastFocus && Drawer.lastFocus.focus && document.contains(Drawer.lastFocus)) {
        Drawer.lastFocus.focus({ preventScroll: true });
      }
    }
  };

  document.addEventListener('close', function (e) {
    if (e.target && e.target.nodeName === 'DIALOG') {
      var anyOpen = document.querySelector('dialog[open]');
      if (!anyOpen) {
        document.documentElement.style.removeProperty('overflow');
        document.body.classList.remove('has-open-drawer');
        var mainEl = document.querySelector('#MainContent');
        if (mainEl) mainEl.removeAttribute('inert');
        var footEl = document.querySelector('footer, .page__footer, [role="contentinfo"]');
        if (footEl) footEl.removeAttribute('inert');
      }
      $$('[data-hb-drawer-open]').forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
      if (!anyOpen && e.target.hasAttribute('data-hb-drawer')) {
        window.setTimeout(function () {
          var a = document.activeElement;
          if ((!a || a === document.body) && Drawer.lastFocus && document.contains(Drawer.lastFocus)) {
            Drawer.lastFocus.focus({ preventScroll: true });
          }
        }, 0);
      }
      if (e.target.hasAttribute('data-hb-quickview-dialog')) {
        $$('[data-hb-quickview][aria-expanded="true"]').forEach(function (t) {
          t.setAttribute('aria-expanded', 'false');
        });
      }
    }
  }, true);

  document.addEventListener('click', function (e) {
    var searchOpener = e.target.closest('[data-hb-search-open]');
    if (searchOpener) {
      e.preventDefault();
      Drawer.open('search');
      return;
    }

    var expandBtn = e.target.closest('.hb-menu__expand-btn, .hb-menu__link--parent');
    if (expandBtn) {
      e.preventDefault();
      var itemWithSub = expandBtn.closest('.hb-menu__item--has-sub');
      if (itemWithSub) {
        var subList = itemWithSub.querySelector('.hb-menu__sub-list');
        var isExpanded = itemWithSub.classList.toggle('is-expanded');
        itemWithSub.querySelector('.hb-menu__expand-btn').setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        if (subList && isExpanded) {
          window.setTimeout(function () {
            var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            itemWithSub.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
          }, 300);
        }
      }
      return;
    }
    var opener = e.target.closest('[data-hb-drawer-open]');
    if (opener) {
      e.preventDefault();
      var drawerName = opener.getAttribute('data-hb-drawer-open');
      if (drawerName === 'cart' && typeof Cart !== 'undefined') {
        Cart.get();
      }
      Drawer.open(drawerName);
      return;
    }
    if (e.target.closest('[data-hb-drawer-close]')) {
      e.preventDefault();
      Drawer.close(e.target.closest('dialog'));
      return;
    }
    if (e.target && e.target.nodeName === 'DIALOG' && e.target.hasAttribute('open')) {
      var rect = e.target.getBoundingClientRect();
      var inDialog = (
        rect.top <= e.clientY && e.clientY <= rect.top + rect.height &&
        rect.left <= e.clientX && e.clientX <= rect.left + rect.width
      );
      if (!inDialog) {
        Drawer.close(e.target);
      }
    }
  });

  /* ---------------------------------------------------------------------
     Cart
     --------------------------------------------------------------------- */
  var Cart = {
    busy: false,

    headerCount: function () {
      return $$('[data-hb-cart-count]');
    },

    syncCount: function (count) {
      Cart.headerCount().forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
    },

    shipping: function (total) {
      if (typeof total !== 'number' || isNaN(total)) return;
      var bar = $('[data-hb-shipping-bar]');
      if (!bar) return;
      var threshold = parseInt(bar.getAttribute('data-threshold'), 10) || ((window.HB && window.HB.settings && window.HB.settings.freeShipping && window.HB.settings.freeShipping.threshold) || 500000);
      var fill = $('[data-hb-shipping-fill]', bar);
      var text = $('[data-hb-shipping-text]', bar);
      var remaining = threshold - total;
      var pct = Math.min(100, Math.max(0, (total / threshold) * 100));
      if (fill) fill.style.width = pct + '%';
      bar.classList.toggle('is-reached', remaining <= 0);
      if (text) {
        if (remaining > 0) {
          text.textContent = 'KARGO ÜCRETSİZ İÇİN ' + money(remaining) + ' KALDI';
        } else {
          var defaultReached = (Math.round(threshold / 100)) + 'TL ÜZERİ TÜM ALIŞVERİŞLERDE KARGO ÜCRETSİZ';
          text.textContent = bar.getAttribute('data-reached-text') || defaultReached;
        }
      }
    },

    knownLimits: {},

    getInventory: function () {
      var el = $('[data-hb-cart-inv]');
      if (el) {
        try {
          var map = JSON.parse(el.textContent);
          for (var k in map) {
            if (map.hasOwnProperty(k)) Cart.knownLimits[k] = map[k];
          }
        } catch (e) {}
      }
      return Cart.knownLimits;
    },

    notice: function (msg) {
      var el = $('[data-hb-cart-notice]');
      if (!el) {
        var body = $('[data-hb-cart-body]');
        if (body && body.parentNode) {
          el = document.createElement('div');
          el.className = 'hb-cart__notice';
          el.setAttribute('data-hb-cart-notice', '');
          body.parentNode.insertBefore(el, body);
        }
      }
      if (el) {
        el.textContent = msg || '';
        el.hidden = !msg;
      }
    },

    render: function (payload) {
      if (!payload || typeof payload !== 'object' || payload.errors || !Array.isArray(payload.items)) {
        return;
      }

      Cart.syncCount(payload.item_count);
      Cart.shipping(payload.total_price);

      var body = $('[data-hb-cart-body]');
      var foot = $('[data-hb-cart-foot]');
      if (!body) return;

      var totalEl = $('[data-hb-cart-total]');
      if (totalEl) totalEl.textContent = money(payload.total_price);

      var shipBar = $('[data-hb-shipping-bar]');
      if (!payload.items.length) {
        if (shipBar) shipBar.hidden = true;
        body.innerHTML =
          '<div class="hb-empty">' +
            '<p class="hb-empty__text">' + escapeHtml(strings.cartEmpty || 'Sepetiniz boş.') + '</p>' +
            '<a href="/collections/all" class="hb-empty__link">' + escapeHtml(strings.continueShopping || 'ALIŞVERİŞE BAŞLA') + ' &rarr;</a>' +
          '</div>';
        if (foot) foot.hidden = true;
        Cart._qtyByKey = {};
        emit('hb:cart:updated', { cart: payload });
        return;
      }

      if (shipBar) shipBar.hidden = false;
      if (foot) foot.hidden = false;

      var invMap = Cart.getInventory();
      var prevQty = Cart._qtyByKey || null;
      var nextQty = {};
      var html = '<ul class="hb-cart__list">';
      payload.items.forEach(function (item, i) {
        var line = i + 1;
        var maxQty = invMap[item.variant_id] != null ? invMap[item.variant_id] : (Cart.knownLimits[item.variant_id] || 9999);
        var isMax = (item.quantity >= maxQty);
        var upDisabled = isMax ? ' aria-disabled="true" data-inv-limit="' + maxQty + '"' : '';

        var imgSrc = '';
        if (typeof item.image === 'string' && item.image) {
          imgSrc = item.image;
        } else if (item.featured_image && item.featured_image.url) {
          imgSrc = item.featured_image.url;
        } else if (item.image && item.image.url) {
          imgSrc = item.image.url;
        } else if (item.image && item.image.src) {
          imgSrc = item.image.src;
        } else if (typeof item.featured_image === 'string' && item.featured_image) {
          imgSrc = item.featured_image;
        }

        var imgHtml = imgSrc
          ? '<img src="' + escapeHtml(shopifyImgUrl(imgSrc, 240)) + '" srcset="' + escapeHtml(shopifyImgUrl(imgSrc, 240)) + ' 1x, ' + escapeHtml(shopifyImgUrl(imgSrc, 480)) + ' 2x" alt="' + escapeHtml(item.product_title || '') + '" width="96" height="120" decoding="async">'
          : '';

        var key = String(item.key || item.variant_id);
        nextQty[key] = item.quantity;
        var rowState = '';
        if (prevQty) {
          if (!(key in prevQty)) rowState = ' is-entering';
          else if (prevQty[key] !== item.quantity) rowState = ' is-changed';
        }

        html +=
          '<li class="hb-cart__item' + rowState + '" data-hb-cart-line="' + line + '">' +
            '<a href="' + item.url + '" class="hb-cart__media">' +
              imgHtml +
            '</a>' +
            '<div class="hb-cart__info">' +
              '<a href="' + item.url + '" class="hb-cart__title">' + escapeHtml(item.product_title) + '</a>' +
              (item.variant_title && item.variant_title !== 'Default Title'
                ? '<p class="hb-cart__variant">' + escapeHtml(item.variant_title) + '</p>' : '') +
              '<div class="hb-cart__row">' +
                '<div class="hb-qty" data-hb-qty data-line="' + line + '" data-variant-id="' + item.variant_id + '">' +
                  '<button type="button" class="hb-qty__btn" data-hb-qty-down aria-label="Azalt">&minus;</button>' +
                  '<input class="hb-qty__input" type="number" inputmode="numeric" value="' + item.quantity +
                    '" min="0" data-hb-qty-input aria-label="Adet">' +
                  '<button type="button" class="hb-qty__btn" data-hb-qty-up' + upDisabled + ' aria-label="Artır">&plus;</button>' +
                '</div>' +
                '<span class="hb-cart__price" data-hb-line-price="' + line + '">' + money(item.final_line_price) + '</span>' +
              '</div>' +
            '</div>' +
            '<button type="button" class="hb-cart__remove" data-hb-cart-remove="' + line +
              '" aria-label="Sil">&times;</button>' +
          '</li>';
      });
      html += '</ul>';
      body.innerHTML = html;
      Cart._qtyByKey = nextQty;
      emit('hb:cart:updated', { cart: payload });
    },

    get: function () {
      var cartUrl = routes.cart || '/cart';
      if (!cartUrl.endsWith('.js')) cartUrl += '.js';
      return fetch(cartUrl, { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) return null;
          return r.json().catch(function () { return null; });
        })
        .then(function (cart) {
          if (cart && typeof cart === 'object') Cart.render(cart);
          return cart;
        })
        .catch(function () {
          return null;
        });
    },

    change: function (line, quantity) {
      Cart._queue = Cart._queue || Promise.resolve();
      var p = Cart._queue.then(function() {
        var priceEl = $('[data-hb-line-price="' + line + '"]');
        if (priceEl) priceEl.style.opacity = '0.35';
        var rowEl = $('[data-hb-cart-line="' + line + '"]');
        if (rowEl) rowEl.setAttribute('aria-busy', 'true');
        if (rowEl && quantity === 0) rowEl.classList.add('is-pending');
  
        var payload = { line: line, quantity: quantity };
        var changeUrl = (routes.cartChange || '/cart/change');
        if (!changeUrl.endsWith('.js')) changeUrl += '.js';
  
        return fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (r) {
          /* Shopify answers cart requests as text/javascript, so the body is
             parsed rather than trusting the content type. A body that is not
             JSON (an error page, a dropped connection) is a failed request. */
          return r.text().then(function (text) {
            var data;
            try { data = JSON.parse(text); } catch (e) { data = null; }
            if (!data || typeof data !== 'object') {
              var netErr = new Error('İşlem tamamlanamadı');
              netErr.hbNetwork = true;
              throw netErr;
            }
            return { ok: r.ok, data: data };
          });
        })
        .then(function (res) {
          
          if (priceEl) priceEl.style.opacity = '1';

          if (!res.ok || res.data.errors || res.data.message || !res.data.items) {
            var wrap = $('[data-line="' + line + '"]');
            var varId = wrap ? wrap.getAttribute('data-variant-id') : null;
            var input = wrap ? $('[data-hb-qty-input]', wrap) : null;
            var curQty = input ? parseInt(input.value, 10) : 1;
            var limitQty = Math.max(1, curQty > 1 ? curQty - 1 : 1);
            if (varId) {
              Cart.knownLimits[varId] = limitQty;
            }
            /* Shopify states the real reason and the real quantity; prefer it. */
            var noticeMsg = (res.data && res.data.description) || ('BU ÜRÜNDEN STOKTA YALNIZCA ' + limitQty + ' ADET VAR.');
            announce(noticeMsg);
            Cart.notice(noticeMsg);
            return Cart.get();
          }

          Cart.notice('');
          Cart.render(res.data);
          announce(quantity === 0 ? strings.removed : (strings.cartUpdated || strings.removed));
          return res.data;
        })
        .catch(function (err) {
          /* A failed request is not a stock limit. Say what actually happened
             and redraw the cart from the server so the UI matches reality. */
          if (priceEl) priceEl.style.opacity = '1';
          var failMsg = strings.cartError || 'Bir hata oluştu';
          announce(failMsg);
          Cart.notice(failMsg);
          emit('hb:cart:error', { line: line, quantity: quantity });
          return Cart.get();
        });
      });
      Cart._queue = p.catch(function(){});
      return p;
    },

    add: function (variantId, quantity, opts) {
      opts = opts || {};
      return fetch(routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
      })
        .then(function (r) {
          return r.json().then(function (data) {
            return { ok: r.ok, status: r.status, data: data };
          });
        })
        .then(function (res) {
          if (!res.ok || res.data.status === 422 || res.data.errors) {
            /* Shopify explains a refused add in `description` (for example how
               many are already in the cart). Show that, not a guessed number. */
            var msg = (res.data && (res.data.description || res.data.message)) || strings.cartError || 'Bir hata oluştu';
            var refused = new Error(msg);
            refused.hbHandled = true;
            announce(msg);
            Cart.notice(msg);
            emit('hb:cart:error', { variantId: variantId, message: msg });
            if (!opts.deferDrawer) Drawer.open('cart');
            throw refused;
          }
          announce(strings.addedToCart);
          return Cart.get().then(function (fullCart) {
            emit('hb:cart:added', { variantId: variantId, cart: fullCart || null, source: opts.source || 'form' });
            if (!opts.deferDrawer) Drawer.open('cart');
            return fullCart || res.data;
          });
        })
        .catch(function (err) {
          if (err && err.hbHandled) throw err;
          var failMsg = strings.cartError || 'Bir hata oluştu';
          announce(failMsg);
          emit('hb:cart:error', { variantId: variantId, message: failMsg });
          var failed = new Error(failMsg);
          failed.hbHandled = true;
          throw failed;
        });
    }
  };

  window.HBCart = Cart;

  /* Every confirmed add lands on the header bag. The PDP runs its own richer
     flight and announces source "pdp", so it is skipped here. */
  document.addEventListener('hb:cart:added', function (e) {
    if (e.detail && e.detail.source === 'pdp') return;
    $$('[data-hb-cart-toggle]').forEach(function (t) {
      t.classList.remove('is-landed');
      void t.offsetWidth;
      t.classList.add('is-landed');
      window.setTimeout(function () { t.classList.remove('is-landed'); }, 700);
    });
  });

  function handleDisabledQtyPress(btn, e) {
    if (!btn) return false;
    var isDisabled = btn.getAttribute('aria-disabled') === 'true' || btn.disabled;
    if (!isDisabled) return false;
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    var wrap = btn.closest('[data-hb-qty]');
    var input = wrap && $('[data-hb-qty-input]', wrap);
    var varId = wrap && wrap.getAttribute('data-variant-id');
    var limitQty = parseInt(btn.getAttribute('data-inv-limit'), 10) ||
                   (varId && Cart.knownLimits[varId]) ||
                   (input ? parseInt(input.value, 10) : 1) || 1;
    var msg = 'BU ÜRÜNDEN STOKTA YALNIZCA ' + limitQty + ' ADET VAR.';
    announce(msg);
    Cart.notice(msg);

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      btn.classList.remove('is-shaking');
      void btn.offsetWidth;
      btn.classList.add('is-shaking');
      setTimeout(function () {
        btn.classList.remove('is-shaking');
      }, 120);
    }
    return true;
  }

  document.addEventListener('pointerdown', function (e) {
    var up = e.target.closest('[data-hb-qty-up]');
    if (up && handleDisabledQtyPress(up, e)) {
      return;
    }
  });

  document.addEventListener('click', function (e) {
    var remove = e.target.closest('[data-hb-cart-remove]');
    if (remove) {
      e.preventDefault();
      var line = parseInt(remove.getAttribute('data-hb-cart-remove'), 10);
      var itemEl = remove.closest('.hb-cart__item');
      if (itemEl) itemEl.classList.add('is-pending');
      Cart.change(line, 0);
      return;
    }
    var up = e.target.closest('[data-hb-qty-up]');
    if (up && handleDisabledQtyPress(up, e)) {
      return;
    }
    var down = e.target.closest('[data-hb-qty-down]');
    if (up || down) {
      var wrap = (up || down).closest('[data-hb-qty]');
      var input = wrap && $('[data-hb-qty-input]', wrap);
      if (!input) return;
      var line = parseInt(wrap.getAttribute('data-line'), 10);
      var next = parseInt(input.value, 10) + (up ? 1 : -1);
      if (next < 0) next = 0;
      input.value = next;
      Cart.change(line, next);
    }
  });

  document.addEventListener('change', function (e) {
    if (!e.target.matches('[data-hb-qty-input]')) return;
    var wrap = e.target.closest('[data-hb-qty]');
    if (!wrap) return;
    var line = parseInt(wrap.getAttribute('data-line'), 10);
    var qty = parseInt(e.target.value, 10);
    if (isNaN(qty) || qty < 0) qty = 0;
    e.target.value = qty;
    Cart.change(line, qty);
  });

  /* Product forms: progressive enhancement over a real Shopify form. */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest(PRODUCT_FORM);
    if (!form) return;
    e.preventDefault();

    var btn = $('[data-hb-atc]', form);
    var idInput = $('[data-hb-variant-id]', form);
    var qtyInput = $('[data-hb-qty-input]', form);

    if (idInput && (!idInput.value || idInput.value === 'null')) {
      announce(strings.unavailable);
      return;
    }
    var idleLabel = btn ? (btn.getAttribute('data-label') || btn.textContent) : '';
    if (btn) { btn.disabled = true; Feedback.set(btn, 'loading', strings.adding || strings.loading || '…'); }

    Cart.add(
      idInput ? idInput.value : null,
      qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1
    ).then(function () {
      if (btn) { btn.disabled = false; Feedback.set(btn, 'idle', idleLabel); }
    }).catch(function () {
      if (btn) {
        btn.disabled = false;
        Feedback.set(btn, 'error', idleLabel);
        window.setTimeout(function () { Feedback.set(btn, 'idle'); }, 600);
      }
    });
  });

  /* ---------------------------------------------------------------------
     Variant selection
     Uses Shopify's option values to map a choice to a variant. No guesswork.
     --------------------------------------------------------------------- */
  window.HBVariants = {
    update: function (product, selected) {
      var variant = product.variants.filter(function (v) {
        return v.options.every(function (opt, i) {
          return String(opt).toLowerCase() === String(selected[i]).toLowerCase();
        });
      })[0];

      var form = $(PRODUCT_FORM);
      if (!form) return null;

      var idInput = $('[data-hb-variant-id]', form);
      var btn = $('[data-hb-atc]', form);

      $$('[data-hb-option-value]').forEach(function (input) {
        var wrap = input.closest('[data-hb-option]');
        if (wrap) {
          var on = String(input.value).toLowerCase() === String(selected[input.getAttribute('data-hb-index')]).toLowerCase();
          wrap.classList.toggle('is-active', on);
        }
      });

      var priceEl = $('[data-hb-price]');
      var compareEl = $('[data-hb-price-compare]');
      var saleFlag = $('[data-hb-sale-flag]');

      if (variant) {
        if (idInput) idInput.value = variant.id;
        if (priceEl) priceEl.textContent = money(variant.price);
        if (compareEl) {
          if (variant.compare_at_price > variant.price) {
            compareEl.textContent = money(variant.compare_at_price);
            compareEl.hidden = false;
            if (saleFlag) saleFlag.hidden = false;
          } else {
            compareEl.hidden = true;
            if (saleFlag) saleFlag.hidden = true;
          }
        }
        if (btn) {
          btn.disabled = !variant.available;
          btn.textContent = variant.available
            ? (btn.getAttribute('data-label') || strings.addToCart)
            : strings.soldOut;
        }
        // The mobile sticky bar shows the same variant's price and stock state.
        if (window.HBStickyATC) window.HBStickyATC.sync(variant, strings);
      }
      return variant;
    }
  };

  /* ---------------------------------------------------------------------
     MEDIA MODULE

     Owns frame selection for the product gallery. It reads the markup
     contract documented in sections/main-product.liquid and nothing else:
     frames are addressed by data-hb-media-index, thumbs write
     data-hb-media-to, and --media-index on the root records the visible
     frame. A later image-sequence or transition enhancement replaces the
     body of show() and inherits the same contract, so the PDP is not
     rewritten to add motion.

     show() only toggles the hidden attribute. It does not animate, does not
     preload, and does not read any motion preference: visibility is not
     motion, and reduced-motion visitors get the same instant frame change.
     --------------------------------------------------------------------- */
  var Media = {
    init: function () {
      var root = $('[data-hb-media]');
      if (!root) return;

      var frames = $$('[data-hb-media-frame]', root);
      var thumbs = $$('[data-hb-media-thumb]', root);
      if (!frames.length) return;

      function show(index) {
        var i = Math.max(0, Math.min(frames.length - 1, index | 0));
        frames.forEach(function (f, n) {
          f.hidden = n !== i;
        });
        thumbs.forEach(function (t, n) {
          t.setAttribute('aria-selected', n === i ? 'true' : 'false');
        });
        root.style.setProperty('--media-index', i);
        return i;
      }

      root.hbShow = show;
      show(0);

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var to = parseInt(thumb.getAttribute('data-hb-media-to'), 10);
          if (!isNaN(to)) show(to);
        });
      });

      // Arrow keys move between frames, matching the tablist semantics the
      // markup already declares.
      root.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        var current = parseInt(root.style.getPropertyValue('--media-index'), 10) || 0;
        var next = e.key === 'ArrowRight' ? current + 1 : current - 1;
        if (next < 0 || next >= frames.length) return;
        e.preventDefault();
        show(next);
        if (thumbs[next]) thumbs[next].focus();
      });
    }
  };

  /* ---------------------------------------------------------------------
     STICKY PURCHASE BAR

     The client's requirement: the primary action is reachable from any
     scroll position, without the title, options and action having to share
     the first viewport. The bar appears once the real add-to-cart has left
     the viewport, so it never duplicates a control that is already visible.

     The bar's button submits the real product form rather than posting on
     its own, so there is exactly one cart code path and the quantity the
     shopper chose on the page is the quantity that reaches the cart. With
     JavaScript disabled the bar never appears and the inline form is the
     whole purchase path, which is the correct progressive-enhancement
     fallback.
     --------------------------------------------------------------------- */
  var StickyATC = {
    init: function () {
      var bar = $('[data-hb-sticky-atc]');
      var form = $(PRODUCT_FORM);
      if (!bar || !form) return;

      var realBtn = $('[data-hb-atc]', form);
      var priceEl = $('[data-hb-sticky-price]', bar);
      var barBtn = $('[data-hb-sticky-add]', bar);
      if (!realBtn || !barBtn) return;

      function inView(el) {
        var r = el.getBoundingClientRect();
        return r.top < window.innerHeight && r.bottom > 0;
      }

      var ticking = false;
      function update() {
        bar.hidden = inView(realBtn);
        ticking = false;
      }

      window.addEventListener('scroll', function () {
        if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
      }, { passive: true });

      window.addEventListener('resize', function () {
        if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
      }, { passive: true });

      barBtn.addEventListener('click', function () {
        if (barBtn.disabled) return;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      });

      // Keep the bar's price and availability in step with the real form.
      var basePrice = $('[data-hb-price]', form);
      if (basePrice && priceEl) {
        var sync = new MutationObserver(function () {
          priceEl.textContent = basePrice.textContent;
        });
        sync.observe(basePrice, { childList: true, characterData: true, subtree: true });
      }

      window.HBStickyATC = {
        sync: function (variant, stringsRef) {
          if (!variant) return;
          if (priceEl && typeof variant.price !== 'undefined') {
            priceEl.textContent = money(variant.price);
          }
          barBtn.disabled = !variant.available;
          barBtn.textContent = variant.available
            ? (barBtn.getAttribute('data-label') || strings.addToCart)
            : (stringsRef && stringsRef.soldOut) || barBtn.textContent;
        }
      };

      update();
    }
  };

  document.addEventListener('change', function (e) {
    if (!e.target.matches('[data-hb-option-value]')) return;
    var holder = e.target.closest('[data-hb-product]');
    if (!holder) return;
    var json = $('[data-hb-product-json]', holder);
    if (!json) return;
    var product;
    try { product = JSON.parse(json.textContent); } catch (err) { return; }

    var selected = [];
    $$('[data-hb-option-value]', holder).forEach(function (input) {
      if (input.checked) selected[parseInt(input.getAttribute('data-hb-index'), 10)] = input.value;
      else if (!input.checked && input.tagName === 'SELECT') selected[parseInt(input.getAttribute('data-hb-index'), 10)] = input.value;
    });

    window.HBVariants.update(product, selected);
  });

  /* ---------------------------------------------------------------------
     Wishlist — keeps the legacy localStorage key
     --------------------------------------------------------------------- */
  var Wishlist = {
    key: 'localWishlist',
    get: function () { return store(Wishlist.key) || []; },
    set: function (list) { store(Wishlist.key, list); Wishlist.render(); },
    toggle: function (handle) {
      var list = Wishlist.get();
      var i = list.indexOf(handle);
      if (i === -1) list.push(handle); else list.splice(i, 1);
      Wishlist.set(list);
      announce(i === -1 ? (strings.addedToWishlist || '') : strings.removed);
      var drawer = $('[data-hb-drawer="favorites"]');
      if (drawer && drawer.hasAttribute('open')) {
        Wishlist.loadDrawer();
      }
    },
    remove: function (handle, itemEl) {
      var list = Wishlist.get().filter(function (h) { return h !== handle; });
      Wishlist.set(list);
      if (itemEl) {
        itemEl.classList.add('is-removing');
        window.setTimeout(function () {
          itemEl.remove();
          var listEl = $('[data-hb-favorites-list]');
          if (!listEl || listEl.children.length === 0) {
            var emptyEl = $('[data-hb-favorites-empty]');
            if (emptyEl) emptyEl.hidden = false;
          }
        }, 200);
      }
    },
    render: function () {
      var list = Wishlist.get();
      $$('[data-hb-wishlist-count]').forEach(function (el) {
        el.textContent = list.length;
        el.hidden = list.length === 0;
      });
      $$('[data-hb-wishlist]').forEach(function (btn) {
        var on = list.indexOf(btn.getAttribute('data-hb-wishlist')) !== -1;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        var g = btn.querySelector('.hb-glyph');
        if (g) g.classList.toggle('is-filled', on);
      });
    },
    loadDrawer: function () {
      var list = Wishlist.get();
      var listEl = $('[data-hb-favorites-list]');
      var emptyEl = $('[data-hb-favorites-empty]');
      var loadingEl = $('[data-hb-favorites-loading]');
      if (!listEl || !emptyEl) return;

      if (list.length === 0) {
        listEl.innerHTML = '';
        emptyEl.hidden = false;
        if (loadingEl) loadingEl.hidden = true;
        return;
      }

      emptyEl.hidden = true;
      if (loadingEl) loadingEl.hidden = false;

      var validProducts = [];
      var missingHandles = [];
      var promises = list.map(function (handle) {
        return fetch('/products/' + encodeURIComponent(handle) + '.js')
          .then(function (res) {
            if (!res.ok) {
              missingHandles.push(handle);
              return null;
            }
            return res.json();
          })
          .then(function (prod) {
            if (prod) validProducts.push(prod);
          })
          .catch(function () {
            missingHandles.push(handle);
          });
      });

      Promise.all(promises).then(function () {
        if (loadingEl) loadingEl.hidden = true;

        if (missingHandles.length > 0) {
          var cleanedList = list.filter(function (h) { return missingHandles.indexOf(h) === -1; });
          Wishlist.set(cleanedList);
        }

        if (validProducts.length === 0) {
          listEl.innerHTML = '';
          emptyEl.hidden = false;
          return;
        }

        emptyEl.hidden = true;

        listEl.innerHTML = validProducts.map(function (p) {
          var featuredImg = p.featured_image || (p.images && p.images[0]) || '';
          var imgHtml = featuredImg
            ? '<img src="' + escapeHtml(shopifyImgUrl(featuredImg, 240)) + '" srcset="' + escapeHtml(shopifyImgUrl(featuredImg, 240)) + ' 1x, ' + escapeHtml(shopifyImgUrl(featuredImg, 480)) + ' 2x" alt="' + escapeHtml(p.title) + '" width="96" height="120" loading="lazy">'
            : '';
          var priceHtml = money(p.price);
          var availHtml = p.available
            ? ''
            : '<span class="hb-favorites__soldout">TÜKENDİ</span>';

          return (
            '<li class="hb-favorites__item" data-hb-favorites-item="' + escapeHtml(p.handle) + '">' +
              '<a href="' + escapeHtml(p.url) + '" class="hb-favorites__media">' + imgHtml + '</a>' +
              '<div class="hb-favorites__info">' +
                '<a href="' + escapeHtml(p.url) + '" class="hb-favorites__name">' + escapeHtml(p.title) + '</a>' +
                '<div class="hb-favorites__price">' + priceHtml + '</div>' +
                availHtml +
                '<a href="' + escapeHtml(p.url) + '" class="hb-favorites__link">ÜRÜNÜ GÖR</a>' +
              '</div>' +
              '<button type="button" class="hb-favorites__remove" data-hb-favorites-remove="' + escapeHtml(p.handle) + '" aria-label="Favorilerden kaldır">' +
                '<svg class="hb-glyph is-filled" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' +
              '</button>' +
            '</li>'
          );
        }).join('');
      });
    }
  };
  window.HBWishlist = Wishlist;

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-hb-wishlist]');
    if (btn) { e.preventDefault(); Wishlist.toggle(btn.getAttribute('data-hb-wishlist')); return; }

    var removeBtn = e.target.closest('[data-hb-favorites-remove]');
    if (removeBtn) {
      e.preventDefault();
      var handleToRemove = removeBtn.getAttribute('data-hb-favorites-remove');
      var itemRow = removeBtn.closest('.hb-favorites__item');
      Wishlist.remove(handleToRemove, itemRow);
      return;
    }

    var wishlistLink = e.target.closest('a[href*="/pages/wishlist"], [data-hb-wishlist-link]');
    if (wishlistLink) {
      e.preventDefault();
      Wishlist.loadDrawer();
      Drawer.open('favorites');
      return;
    }
  });

  /* ---------------------------------------------------------------------
     Recently viewed — keeps the legacy localRecentlyViewed key
     --------------------------------------------------------------------- */
  var Recent = {
    key: 'localRecentlyViewed',
    max: 8,
    get: function () { return store(Recent.key) || []; },
    add: function (handle, product) {
      if (!handle || !product) return;
      var list = Recent.get().filter(function (h) { return h !== handle; });
      list.unshift(handle);
      store(Recent.key, list.slice(0, Recent.max));
      Recent.render(product);
    },
    render: function (single) {
      var wrap = $('[data-hb-recently-viewed]');
      var grid = $('[data-hb-recently-grid]');
      if (!wrap || !grid) return;
      if (single) {
        wrap.hidden = false;
        grid.innerHTML =
          '<div><a href="' + single.url + '" class="hb-card__media" style="display:block">' +
          '<img src="' + single.featured_image + '" alt="" width="200" height="260" loading="lazy">' +
          '</a><p class="hb-card__title" style="padding-block-start:.75rem">' + escapeHtml(single.title) + '</p></div>';
        return;
      }
      var list = Recent.get();
      if (!list.length) return;
      wrap.hidden = false;
      Promise.all(list.slice(0, Recent.max).map(function (h) {
        return fetch('/products/' + h + '.js').then(function (r) { return r.json(); }).catch(function () { return null; });
      })).then(function (items) {
        grid.innerHTML = items.filter(Boolean).map(function (p) {
          return '<div><a href="' + p.url + '" class="hb-card__media" style="display:block">' +
            '<img src="' + p.featured_image + '" alt="" width="200" height="260" loading="lazy"></a>' +
            '<p class="hb-card__title" style="padding-block-start:.75rem">' + escapeHtml(p.title) + '</p></div>';
        }).join('');
      });
    }
  };
  window.HBRecent = Recent;

  /* ---------------------------------------------------------------------
     Predictive search — Shopify's native endpoint
     --------------------------------------------------------------------- */
  var Search = {
    timer: null,
    run: function (term) {
      var wrap = $('[data-hb-search]');
      if (!wrap) return;
      var grid = $('[data-hb-search-grid]', wrap);
      var count = $('[data-hb-search-count]', wrap);
      var empty = $('[data-hb-search-empty]', wrap);
      var emptyText = $('[data-hb-search-empty-text]', wrap);
      var results = $('[data-hb-search-results]', wrap);
      if (!grid) return;

      if (term.length < 2) {
        if (results) results.hidden = true;
        return;
      }

      var endpoint = (routes.predictiveSearch || '/search/suggest');
      if (endpoint.indexOf('.json') === -1) endpoint += '.json';
      fetch(endpoint + '?q=' + encodeURIComponent(term) +
            '&resources[type]=product&resources[limit]=8')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = (data.resources && data.resources.results && data.resources.results.products) || [];
          if (results) results.hidden = false;
          if (empty) empty.hidden = products.length > 0;
          if (emptyText && products.length === 0) {
            emptyText.textContent = '"' + escapeHtml(term) + '" için sonuç bulunamadı.';
          }
          if (count) count.textContent = products.length ? products.length + ' ürün bulundu' : '';
          grid.innerHTML = products.map(function (p) {
            var imgUrl = (p.featured_image && p.featured_image.url) || p.image || '';
            var img = imgUrl
              ? '<img src="' + imgUrl + '" alt="' + escapeHtml(p.title) + '" width="240" height="300" loading="lazy">'
              : '';
            var rawPrice = parseFloat(p.price);
            var price = (!isNaN(rawPrice) && rawPrice > 0) ? money(Math.round(rawPrice * 100)) : '';
            return '<a href="' + p.url + '" class="hb-search-item">' +
              '<div class="hb-search-item__media">' + img + '</div>' +
              '<div class="hb-search-item__info">' +
                '<p class="hb-search-item__title">' + escapeHtml(p.title) + '</p>' +
                (price ? '<p class="hb-search-item__price">' + price + '</p>' : '') +
              '</div>' +
            '</a>';
          }).join('');
        })
        .catch(function () {
          if (results) results.hidden = true;
        });
    }
  };

  document.addEventListener('input', function (e) {
    if (!e.target.matches('[data-hb-search-input]')) return;
    clearTimeout(Search.timer);
    var term = e.target.value.trim();
    Search.timer = setTimeout(function () { Search.run(term); }, 220);
  });

  /* ---------------------------------------------------------------------
     Quick view
     --------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-hb-quickview]');
    if (!btn) return;
    var handle = btn.getAttribute('data-hb-quickview');
    var dlg = $('[data-hb-quickview-dialog]');
    var body = $('[data-hb-quickview-body]', dlg);
    if (!dlg || !body) return;

    /* A frame with a running thread at the size the content will take, so the
       dialog does not jump when the product arrives. */
    body.innerHTML =
      '<div class="hb-quickview__skeleton" role="status">' +
        '<div class="hb-quickview__skeleton-frame"></div>' +
        '<span class="sr-only">' + escapeHtml(strings.loading || 'Yükleniyor') + '</span>' +
      '</div>';
    Drawer.open('quickview');
    btn.setAttribute('aria-expanded', 'true');

    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var price = money(p.price);
        var compare = p.compare_at_price > p.price
          ? '<s class="hb-price__compare">' + money(p.compare_at_price) + '</s>' : '';

        var hasVariants = Array.isArray(p.variants) && (p.variants.length > 1 || (p.variants[0] && p.variants[0].title !== 'Default Title'));
        var selectedVariant = null;

        /* Pill text comes from Shopify's own option values. When the size is the
           only option that varies, show the size alone ("S"), not "BORDO / S". */
        var sizeIdx = -1;
        (p.options || []).forEach(function (opt, oi) {
          var optName = String((opt && opt.name) || opt || '').toLowerCase();
          if (sizeIdx === -1 && (optName.indexOf('beden') !== -1 || optName.indexOf('size') !== -1)) sizeIdx = oi;
        });
        var sizeOnly = sizeIdx !== -1 && p.variants.every(function (v) {
          return v.options.every(function (val, oi) {
            return oi === sizeIdx || val === p.variants[0].options[oi];
          });
        });

        var pillsHtml = '';
        if (hasVariants) {
          p.variants.forEach(function (v) {
            var disabledAttr = v.available ? '' : ' disabled aria-disabled="true"';
            var titleDisplay = escapeHtml(sizeOnly ? v.options[sizeIdx] : v.title);
            pillsHtml +=
              '<button type="button" role="radio" aria-checked="false" aria-label="' + escapeHtml(v.title) + '" class="hb-quickview__size-pill" data-hb-qv-variant="' + v.id + '"' +
                ' data-price="' + v.price + '"' +
                (v.compare_at_price ? ' data-compare="' + v.compare_at_price + '"' : '') +
                (v.featured_image && v.featured_image.src ? ' data-img="' + escapeHtml(v.featured_image.src) + '"' : '') +
                disabledAttr + '>' +
                titleDisplay +
              '</button>';
          });
        } else if (p.variants && p.variants[0]) {
          selectedVariant = p.variants[0];
        }

        var atcLabel = hasVariants ? 'BEDEN SEÇİNİZ' : (selectedVariant && selectedVariant.available ? (strings.addToCart || 'SEPETE EKLE') : 'TÜKENDİ');
        var atcDisabled = hasVariants || !selectedVariant || !selectedVariant.available ? ' disabled' : '';

        body.innerHTML =
          '<div class="hb-quickview__content">' +
            '<div class="hb-quickview__header">' +
              '<div class="hb-quickview__thumb">' +
                (p.featured_image
                  ? '<img src="' + escapeHtml(shopifyImgUrl(p.featured_image, 240)) + '" srcset="' + escapeHtml(shopifyImgUrl(p.featured_image, 240)) + ' 1x, ' + escapeHtml(shopifyImgUrl(p.featured_image, 480)) + ' 2x" alt="' + escapeHtml(p.title) + '" width="88" height="117" decoding="async" data-hb-qv-img>'
                  : '') +
              '</div>' +
              '<div class="hb-quickview__meta">' +
                '<h2 class="hb-quickview__title">' + escapeHtml(p.title) + '</h2>' +
                '<div class="hb-quickview__price-row">' +
                  '<span class="hb-quickview__price" data-hb-qv-price>' + price + '</span> ' + compare +
                '</div>' +
              '</div>' +
            '</div>' +

            (hasVariants ? (
              '<div class="hb-quickview__sizes-section">' +
                '<div class="hb-quickview__size-header">' +
                  '<span>BEDEN SEÇİN</span>' +
                  '<span class="hb-quickview__selected-badge" data-hb-qv-badge>Lütfen seçiniz</span>' +
                '</div>' +
                '<div class="hb-quickview__size-list" role="radiogroup" aria-label="Beden">' +
                  pillsHtml +
                '</div>' +
              '</div>'
            ) : '') +

            '<button type="button" class="hb-quickview__atc-btn" data-hb-qv-atc data-hb-feedback data-state="idle"' + atcDisabled + '>' +
              atcLabel +
            '</button>' +
            '<p class="hb-quickview__error" data-hb-qv-error role="alert" hidden></p>' +

            '<a href="' + p.url + '" class="hb-quickview__details-link">Ürün detaylarını gör &rarr;</a>' +
          '</div>';

        var content = $('.hb-quickview__content', body);
        if (content) {
          content.addEventListener('click', function (evt) {
            var pill = evt.target.closest('[data-hb-qv-variant]');
            if (pill && !pill.disabled) {
              var varId = parseInt(pill.getAttribute('data-hb-qv-variant'), 10);
              $$('.hb-quickview__size-pill', content).forEach(function (b) {
                b.classList.remove('is-selected');
                b.setAttribute('aria-checked', 'false');
              });
              pill.classList.add('is-selected');
              pill.setAttribute('aria-checked', 'true');

              selectedVariant = p.variants.find(function (v) { return v.id === varId; });
              if (selectedVariant) {
                var badge = $('[data-hb-qv-badge]', content);
                if (badge) badge.textContent = selectedVariant.title;

                var atcBtn = $('[data-hb-qv-atc]', content);
                if (atcBtn) {
                  atcBtn.removeAttribute('disabled');
                  atcBtn.textContent = strings.addToCart || 'SEPETE EKLE';
                }

                var priceEl = $('[data-hb-qv-price]', content);
                if (priceEl) priceEl.textContent = money(selectedVariant.price);

                var imgEl = $('[data-hb-qv-img]', content);
                if (selectedVariant.featured_image && selectedVariant.featured_image.src && imgEl) {
                  imgEl.removeAttribute('srcset');
                  imgEl.src = shopifyImgUrl(selectedVariant.featured_image.src, 480);
                }
              }
            }

            var atcClick = evt.target.closest('[data-hb-qv-atc]');
            if (atcClick && !atcClick.disabled && selectedVariant) {
              /* loading -> (server says yes) success -> cart drawer.
                 On a refusal the dialog stays open and says why. */
              var idleLabel = strings.addToCart || 'SEPETE EKLE';
              var errEl = $('[data-hb-qv-error]', content);
              if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
              atcClick.disabled = true;
              Feedback.set(atcClick, 'loading', (strings.adding || 'EKLENİYOR').toLocaleUpperCase('tr-TR'));
              Cart.add(selectedVariant.id, 1, { deferDrawer: true, source: 'quickview' })
                .then(function () {
                  Feedback.set(atcClick, 'success', (strings.added || 'EKLENDİ').toLocaleUpperCase('tr-TR'));
                  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                  window.setTimeout(function () { Drawer.open('cart'); }, reduce ? 0 : 520);
                })
                .catch(function (err) {
                  atcClick.disabled = false;
                  Feedback.set(atcClick, 'error', idleLabel);
                  window.setTimeout(function () { Feedback.set(atcClick, 'idle'); }, 600);
                  if (errEl) {
                    errEl.textContent = (err && err.message) || strings.cartError || '';
                    errEl.hidden = false;
                  }
                });
            }
          });
        }

        Recent.add(p.handle, p);
      })
      .catch(function () {
        body.innerHTML = '<p class="hb-quickview__loading">' + escapeHtml(strings.cartError || 'Bir hata oluştu.') + '</p>';
      });
  });

  /* ---------------------------------------------------------------------
     Cookie notice
     --------------------------------------------------------------------- */
  var Cookie = {
    key: 'localCookie',
    init: function () {
      var el = $('[data-hb-cookie]');
      if (!el) return;
      if (store(Cookie.key)) { el.hidden = true; return; }
      el.hidden = false;
    },
    accept: function () {
      store(Cookie.key, true);
      var el = $('[data-hb-cookie]');
      if (el) el.hidden = true;
    }
  };

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-hb-cookie-accept]')) Cookie.accept();
  });

  /* ---------------------------------------------------------------------
     Sticky header compression & mobile scroll hide/reveal
     --------------------------------------------------------------------- */
  var StickyHeader = {
    tempTimer: null,
    userScrolledDuringReveal: false,
    revealTemporarily: function (duration, withOvershoot) {
      var head = $('[data-hb-header]');
      if (!head) return;
      StickyHeader.userScrolledDuringReveal = false;
      if (withOvershoot && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        head.classList.add('has-overshoot');
        setTimeout(function () { head.classList.remove('has-overshoot'); }, 300);
      }
      head.classList.remove('is-header-hidden');
      if (StickyHeader.tempTimer) clearTimeout(StickyHeader.tempTimer);
      StickyHeader.tempTimer = setTimeout(function () {
        if (!StickyHeader.userScrolledDuringReveal && window.scrollY > 80 && window.innerWidth <= 749) {
          if (!document.querySelector('dialog[open], .hb-dialog[open]')) {
            head.classList.add('is-header-hidden');
          }
        }
      }, duration || 2400);
    },
    init: function () {
      var head = $('[data-hb-header]');
      if (!head) return;
      var sec = head.closest('.shopify-section');
      if (sec && head.classList.contains('hb-header--sticky')) {
        sec.style.position = 'sticky';
        sec.style.top = '0';
        sec.style.zIndex = '50';
        sec.style.pointerEvents = 'none';
      }
      var lastScrollY = window.scrollY;
      var ticking = false;

      function isDrawerOpen() {
        return !!document.querySelector('dialog[open], .hb-dialog[open], .hb-drawer.is-open, body.has-open-drawer');
      }

      function update() {
        var curY = window.scrollY;
        head.classList.toggle('is-stuck', curY > 24);

        if (window.innerWidth <= 749) {
          if (curY <= 20) {
            head.classList.remove('is-header-hidden');
          } else if (!isDrawerOpen()) {
            var diff = curY - lastScrollY;
            if (Math.abs(diff) >= 8) {
              StickyHeader.userScrolledDuringReveal = true;
              if (diff > 0 && curY > 80) {
                head.classList.add('is-header-hidden');
              } else if (diff < 0) {
                head.classList.remove('is-header-hidden');
              }
              lastScrollY = curY;
            }
          }
        } else {
          head.classList.remove('is-header-hidden');
        }
        ticking = false;
      }

      window.addEventListener('scroll', function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      }, { passive: true });

      update();
    }
  };

  window.StickyHeader = StickyHeader;

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function boot() {
    Wishlist.render();
    Cookie.init();
    StickyHeader.init();
    StickyATC.init();
    Media.init();
    Cart.syncCount($('[data-hb-cart-count]') ? parseInt($('[data-hb-cart-count]').textContent, 10) || 0 : 0);

    var holder = $('[data-hb-product]');
    if (holder) {
      var json = $('[data-hb-product-json]', holder);
      if (json) {
        try {
          var product = JSON.parse(json.textContent);
          Recent.add(product.handle, {
            url: product.url, title: product.title, featured_image: product.featured_image
          });
          var selected = [];
          $$('[data-hb-option-value]', holder).forEach(function (input) {
            if (input.checked) selected[parseInt(input.getAttribute('data-hb-index'), 10)] = input.value;
          });
          if (selected.length === product.options.length) window.HBVariants.update(product, selected);
        } catch (e) { /* product JSON is optional */ }
      }
    }

  }

  document.addEventListener('shopify:section:load', function () { boot(); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();

